import { useState, useMemo, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    TextField,
    Slider,
    Tabs,
    Tab,
    ToggleButtonGroup,
    ToggleButton,
    Paper,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import {
    Person as PersonIcon,
    Home as HomeIcon,
    DirectionsCar as CarIcon,
    ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ThemeProvider } from '@mui/material/styles';
import { getAppTheme } from '../services/authContext'; // Assuming path
import { useSelector } from 'react-redux';
import SEO from '../components/SEO/SEO';
import { getSeoData } from '../utils/seoConfig';


// --- HELPER COMPONENTS ---
const ValueLabelComponent = (props) => {
    const { children, value } = props;
    return (
        <Tooltip enterTouchDelay={0} placement="top" title={value.toLocaleString('en-IN')}>
            {children}
        </Tooltip>
    );
};

const CalculatorInput = ({ label, value, onChange, unit, min, max, step }) => (
    <Box mb={3}>
        <Typography gutterBottom>{label}</Typography>
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8}>
                <Slider
                    value={typeof value === 'number' ? value : 0}
                    onChange={(e, newValue) => onChange(newValue)}
                    min={min}
                    max={max}
                    step={step}
                    components={{ ValueLabel: ValueLabelComponent }}
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <TextField
                    value={value.toLocaleString('en-IN')}
                    onChange={(e) => {
                        const numericValue = Number(e.target.value.replace(/,/g, ''));
                        if (!isNaN(numericValue)) {
                            onChange(numericValue > max ? max : numericValue);
                        }
                    }}
                    size="small"
                    InputProps={{
                        startAdornment: <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>{unit}</Typography>,
                    }}
                    variant="outlined"
                />
            </Grid>
        </Grid>
    </Box>
);

// --- CALCULATOR LOGIC & COMPONENTS ---

// EMI Calculator
const EMICalculator = ({ config }) => {
    const [loanAmount, setLoanAmount] = useState(config.amount.default);
    const [interestRate, setInterestRate] = useState(config.rate.default);
    const [tenure, setTenure] = useState(config.tenure.default);
    const [tenureUnit, setTenureUnit] = useState('Yr');

    const { emi, totalInterest, totalPayment, principal } = useMemo(() => {
        const p = loanAmount;
        const r = interestRate / 12 / 100;
        const n = tenureUnit === 'Yr' ? tenure * 12 : tenure;
        if (p > 0 && r > 0 && n > 0) {
            const emiVal = (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
            const totalPaymentVal = emiVal * n;
            const totalInterestVal = totalPaymentVal - p;
            return { emi: emiVal, totalInterest: totalInterestVal, totalPayment: totalPaymentVal, principal: p };
        }
        return { emi: 0, totalInterest: 0, totalPayment: 0, principal: p };
    }, [loanAmount, interestRate, tenure, tenureUnit]);

    const chartData = [
        { name: 'Principal Amount', value: principal },
        { name: 'Total Interest', value: totalInterest },
    ];
    const COLORS = ['#5c97c3', '#3f51b5'];

    return (
        <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
                <CalculatorInput label="Loan Amount" value={loanAmount} onChange={setLoanAmount} unit="₹" {...config.amount} />
                <CalculatorInput label="Interest Rate" value={interestRate} onChange={setInterestRate} unit="%" {...config.rate} />
                <Box>
                    <Typography gutterBottom>Loan Tenure</Typography>
                    <ToggleButtonGroup value={tenureUnit} exclusive onChange={(e, val) => val && setTenureUnit(val)} size="small">
                        <ToggleButton value="Yr">Years</ToggleButton>
                        <ToggleButton value="Mo">Months</ToggleButton>
                    </ToggleButtonGroup>
                    <CalculatorInput label="" value={tenure} onChange={setTenure} unit={tenureUnit} {...(tenureUnit === 'Yr' ? config.tenure : config.tenureMonths)} />
                </Box>
            </Grid>
            <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 3, backgroundColor: 'background.accent', borderRadius: '16px' }}>
                    <Typography variant="h5" gutterBottom align="center">Loan Repayment Details</Typography>
                    <Box sx={{ height: 250 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                                    {chartData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                                </Pie>
                                <Tooltip formatter={(value) => `₹${value.toLocaleString('en-IN')}`} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </Box>
                    <Box textAlign="center" mt={2}>
                        <Typography variant="h6">Monthly EMI: <strong>₹{emi.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</strong></Typography>
                        <Typography color="text.secondary">Total Interest: ₹{totalInterest.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</Typography>
                        <Typography color="text.secondary">Total Payment: ₹{totalPayment.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</Typography>
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
};

// Prepayment Calculator
const LoanPrepaymentCalculator = ({ config }) => {
    const [loanAmount, setLoanAmount] = useState(config.amount.default);
    const [interestRate, setInterestRate] = useState(config.rate.default);
    const [tenure, setTenure] = useState(config.tenure.default * 12); // Tenure in months
    const [prepayment, setPrepayment] = useState(config.amount.default * 0.1);
    const [reductionType, setReductionType] = useState('emi');
    const [frequency, setFrequency] = useState('one-time');

    // When user selects "Reduce EMI", force frequency to "One-Time" as it's the standard calculation.
    useEffect(() => {
        if (reductionType === 'emi') {
            setFrequency('one-time');
        }
    }, [reductionType]);

    const { originalEMI, newEMI, interestSaved, tenureReducedMonths } = useMemo(() => {
        const p = loanAmount;
        const r = interestRate / 12 / 100;
        const n = tenure;

        if (p <= 0 || r <= 0 || n <= 0)
            return { originalEMI: 0, newEMI: 0, interestSaved: 0, tenureReducedMonths: 0 };

        const originalEMIVal = (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
        const originalTotalPayment = originalEMIVal * n;
        const originalTotalInterest = originalTotalPayment - p;

        if (prepayment <= 0) {
            return { originalEMI: originalEMIVal, newEMI: originalEMIVal, interestSaved: 0, tenureReducedMonths: 0 };
        }

        if (reductionType === 'emi') {
            // Correct logic for EMI reduction (assumes a one-time prepayment)
            const newPrincipal = p - prepayment;
            if (newPrincipal <= 0) {
                return { originalEMI: originalEMIVal, newEMI: 0, interestSaved: originalTotalInterest, tenureReducedMonths: n };
            }
            
            const newEMIVal = (newPrincipal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
            const newTotalPayment = (newEMIVal * n) + prepayment;
            const interestSavedVal = originalTotalPayment - newTotalPayment;
            
            return { originalEMI: originalEMIVal, newEMI: newEMIVal, interestSaved: interestSavedVal > 0 ? interestSavedVal : 0, tenureReducedMonths: 0 };

        } else { // Tenure reduction logic
            let balance = p;
            let newN = 0;
            let totalInterestPaidWithPrepayment = 0;

            while (balance > 0) {
                newN++;
                const interestForMonth = balance * r;
                let principalForMonth = originalEMIVal - interestForMonth;

                let currentPrepayment = 0;
                if (frequency === 'one-time' && newN === 1) currentPrepayment = prepayment;
                if (frequency === 'monthly') currentPrepayment = prepayment;
                if (frequency === 'quarterly' && newN % 3 === 0) currentPrepayment = prepayment;
                if (frequency === 'yearly' && newN % 12 === 0) currentPrepayment = prepayment;

                balance -= (principalForMonth + currentPrepayment);
                totalInterestPaidWithPrepayment += interestForMonth;
                if (newN > n * 2) break; // Safety break
            }
            
            const interestSavedVal = originalTotalInterest - totalInterestPaidWithPrepayment;
            return { originalEMI: originalEMIVal, newEMI: originalEMIVal, interestSaved: interestSavedVal > 0 ? interestSavedVal : 0, tenureReducedMonths: Math.round(n - newN) };
        }
    }, [loanAmount, interestRate, tenure, prepayment, reductionType, frequency]);


    return (
        <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
                <CalculatorInput label="Outstanding Loan Amount" value={loanAmount} onChange={setLoanAmount} unit="₹" {...config.amount} />
                <CalculatorInput label="Current Interest Rate" value={interestRate} onChange={setInterestRate} unit="%" {...config.rate} />
                <CalculatorInput label="Outstanding Tenure" value={tenure} onChange={setTenure} unit="Months" min={1} max={config.tenureMonths.max} step={1} />
                <CalculatorInput label="Pre-Payment Amount" value={prepayment} onChange={setPrepayment} unit="₹" min={0} max={loanAmount} step={10000} />
                <FormControl fullWidth sx={{ mb: 2 }} disabled={reductionType === 'emi'}>
                    <InputLabel>Prepayment Frequency</InputLabel>
                    <Select value={frequency} label="Prepayment Frequency" onChange={(e) => setFrequency(e.target.value)}>
                        <MenuItem value="one-time">One-Time</MenuItem>
                        <MenuItem value="monthly">Monthly</MenuItem>
                        <MenuItem value="quarterly">Quarterly</MenuItem>
                        <MenuItem value="yearly">Yearly</MenuItem>
                    </Select>
                </FormControl>
                <Box>
                    <Typography gutterBottom>Reduce</Typography>
                    <ToggleButtonGroup value={reductionType} exclusive onChange={(e, val) => val && setReductionType(val)} size="small">
                        <ToggleButton value="emi">EMI</ToggleButton>
                        <ToggleButton value="tenure">Tenure</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            </Grid>
            <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 3, backgroundColor: 'background.accent', borderRadius: '16px', textAlign: 'center' }}>
                    <Typography variant="h5" gutterBottom>Your Savings</Typography>
                    <Box sx={{ bgcolor: '#fff5e6', p: 2, borderRadius: '8px', my: 2 }}>
                        <Typography variant="h6">Your savings on interest is <strong>₹{interestSaved.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</strong></Typography>
                    </Box>
                    <Grid container spacing={1} sx={{ my: 2 }}>
                        <Grid item xs={4}>
                            <Typography color="text.secondary">Your current EMI</Typography>
                            <Typography variant="h6">₹{originalEMI.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography color="text.secondary">{reductionType === 'emi' ? 'EMI reduced by' : 'Tenure reduced by'}</Typography>
                            <Typography variant="h6" color="primary">
                                {reductionType === 'emi'
                                    ? `₹${(originalEMI - newEMI).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                                    : `${tenureReducedMonths} Months`
                                }
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography color="text.secondary">{reductionType === 'emi' ? 'Your new EMI' : 'Your new Tenure'}</Typography>
                            <Typography variant="h6">
                                {reductionType === 'emi'
                                    ? `₹${newEMI.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                                    : `${tenure - tenureReducedMonths} Months`
                                }
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </Grid>
    );
};

// Eligibility Calculator
const EligibilityCalculator = ({ config }) => {
    const [income, setIncome] = useState(80000);
    const [otherEmi, setOtherEmi] = useState(5000);
    const [tenure, setTenure] = useState(config.tenure.default);
    const [interestRate, setInterestRate] = useState(config.rate.default);

    const { eligibleLoan, eligibleEmi } = useMemo(() => {
        const r = interestRate / 12 / 100;
        const n = tenure * 12;
        const maxEmi = (income * 0.55) - otherEmi; // FOIR of 55%
        if (maxEmi > 0 && r > 0 && n > 0) {
            const loanAmt = (maxEmi * (Math.pow(1 + r, n) - 1)) / (r * Math.pow(1 + r, n));
            return { eligibleLoan: loanAmt, eligibleEmi: maxEmi };
        }
        return { eligibleLoan: 0, eligibleEmi: 0 };
    }, [income, otherEmi, tenure, interestRate]);

    return (
        <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
                <CalculatorInput label="Gross Monthly Income" value={income} onChange={setIncome} unit="₹" min={20000} max={1000000} step={5000} />
                <CalculatorInput label="Existing Monthly EMIs" value={otherEmi} onChange={setOtherEmi} unit="₹" min={0} max={200000} step={1000} />
                <CalculatorInput label="Loan Tenure" value={tenure} onChange={setTenure} unit="Yr" {...config.tenure} />
                <CalculatorInput label="Interest Rate" value={interestRate} onChange={setInterestRate} unit="%" {...config.rate} />
            </Grid>
            <Grid item xs={12} md={5}>
                <Paper elevation={0} sx={{ p: 3, backgroundColor: 'background.accent', borderRadius: '16px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center' }}>
                    <Typography variant="h5" gutterBottom>Your Eligibility</Typography>
                    <Box>
                        <Typography variant="h6" color="text.secondary">Eligible EMI per Month</Typography>
                        <Typography variant="h4" color="primary" gutterBottom><strong>₹{eligibleEmi.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</strong></Typography>
                    </Box>
                    <Box mt={3}>
                        <Typography variant="h6" color="text.secondary">Eligible Loan Amount</Typography>
                        <Typography variant="h4" color="primary"><strong>₹{eligibleLoan.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</strong></Typography>
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
};

// --- CONFIGURATION FOR LOAN TYPES ---
const calculatorConfigs = {
    personal: {
        title: "Personal Loan Calculator",
        description: "Plan your finances for travel, weddings, or emergencies. Calculate your EMI, check your eligibility, and see how prepayments can help you save.",
        amount: { default: 500000, min: 50000, max: 4000000, step: 25000 },
        rate: { default: 11, min: 9, max: 22, step: 0.1 },
        tenure: { default: 5, min: 1, max: 7, step: 1 },
        tenureMonths: { default: 60, min: 12, max: 84, step: 1 },
    },
    home: {
        title: "Home Loan Calculator",
        description: "Take the first step towards your dream home. Estimate your monthly payments, determine your loan eligibility, and plan your prepayment strategy.",
        amount: { default: 5000000, min: 500000, max: 50000000, step: 100000 },
        rate: { default: 8.5, min: 7, max: 15, step: 0.05 },
        tenure: { default: 20, min: 5, max: 30, step: 1 },
        tenureMonths: { default: 240, min: 60, max: 360, step: 12 },
    },
    car: {
        title: "Car Loan Calculator",
        description: "Get behind the wheel of your new car sooner. Use our tools to calculate your car loan EMI, check eligibility, and understand prepayment benefits.",
        amount: { default: 800000, min: 100000, max: 5000000, step: 25000 },
        rate: { default: 9.5, min: 8, max: 18, step: 0.1 },
        tenure: { default: 5, min: 1, max: 7, step: 1 },
        tenureMonths: { default: 60, min: 12, max: 84, step: 1 },
    }
};

// --- FAQ Data ---
const faqData = {
    personal: [
        { q: 'What is a personal loan?', a: 'A personal loan is an unsecured loan that you can use for various purposes like weddings, vacations, or medical emergencies.' },
        { q: 'What is the typical interest rate for a personal loan?', a: 'Interest rates for personal loans in India typically range from 10% to 22%, depending on your credit score, income, and the lender.' },
    ],
    home: [
        { q: 'What is a home loan?', a: 'A home loan is a secured loan taken to purchase a residential property. The property itself acts as collateral.' },
        { q: 'What is the maximum tenure for a home loan?', a: 'The maximum tenure for a home loan in India is usually 30 years, subject to the borrower\'s age and retirement date.' },
    ],
    car: [
        { q: 'Can I get 100% financing for a car loan?', a: 'While some lenders may offer up to 100% of the on-road price, most typically finance 80-90% of the ex-showroom price of the car.' },
        { q: 'What is the difference between a new and used car loan?', a: 'New car loans generally have lower interest rates and longer tenures compared to used car loans, as the depreciation on a new car is lower.' },
    ]
};

const FAQ = ({ questions }) => (
    <Box mt={6}>
        <Typography variant="h4" component="h2" align="center" gutterBottom>
            Frequently Asked Questions
        </Typography>
        {questions.map((item, index) => (
            <Accordion key={index}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>{item.q}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography color="text.secondary">{item.a}</Typography>
                </AccordionDetails>
            </Accordion>
        ))}
    </Box>
);


// --- MAIN PAGE COMPONENT ---
const FinancialCalculator = () => {
    const mode = useSelector((state) => state.theme.mode);
    const theme = getAppTheme(mode);
    const [mainTab, setMainTab] = useState('personal');
    const [subTab, setSubTab] = useState(0);
    const seoData = getSeoData("/calc");

    const handleMainTabChange = (event, newValue) => setMainTab(newValue);
    const handleSubTabChange = (event, newValue) => setSubTab(newValue);

    const activeConfig = calculatorConfigs[mainTab];
    const activeFaqs = faqData[mainTab];

    const renderCalculator = () => {
        switch (subTab) {
            case 0: return <EMICalculator config={activeConfig} />;
            case 1: return <LoanPrepaymentCalculator config={activeConfig} />;
            case 2: return <EligibilityCalculator config={activeConfig} />;
            default: return <EMICalculator config={activeConfig} />;
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <SEO {...seoData} />
            <Box sx={{ py: 5, backgroundColor: 'background.default', minHeight: '100vh' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Typography variant="h1" component="h1" gutterBottom sx={{ color: 'heading.h2' }}>
                            EMI Calculators
                        </Typography>
                        <Typography variant="h6" color="text.secondary">
                            {activeConfig.description}
                        </Typography>
                    </Box>

                    <Paper elevation={0} sx={{ borderRadius: '16px', p: 1, mb: 4 }}>
                        <Tabs value={mainTab} onChange={handleMainTabChange} centered variant="fullWidth">
                            <Tab icon={<PersonIcon />} iconPosition="start" label="Personal Loan" value="personal" />
                            <Tab icon={<HomeIcon />} iconPosition="start" label="Home Loan" value="home" />
                            <Tab icon={<CarIcon />} iconPosition="start" label="Car Loan" value="car" />
                        </Tabs>
                    </Paper>

                    <Card>
                        <CardContent sx={{ p: { xs: 2, md: 4 } }}>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                                <Tabs 
                                    value={subTab} 
                                    onChange={handleSubTabChange} 
                                    aria-label="calculator types"
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    allowScrollButtonsMobile
                                >
                                    <Tab label="EMI Calculator" />
                                    <Tab label="Repayment Calculator" />
                                    <Tab label="Eligibility Calculator" />
                                </Tabs>
                            </Box>
                            {renderCalculator()}
                        </CardContent>
                    </Card>
                    <Typography variant="caption" display="block" color="text.secondary" align="center" mt={2}>
                        Disclaimer: This calculator is for educational purposes only. Results are estimates based on the information you provide and may not reflect actual loan offers.
                    </Typography>

                    <FAQ questions={activeFaqs} />
                </Container>
            </Box>
        </ThemeProvider>
    );
};

export default FinancialCalculator
